import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API_BASE, kcFetch, authHeaders } from "./apiClient";

// ============================================================
// AppSettingsContext — single source of truth for brand & label
// settings. Fetched once at app boot from the PUBLIC endpoint so
// login screen and QR poster render with the correct branding
// even before a user signs in.
// ============================================================
const DEFAULTS = {
  appName: "Kingdom Connect",
  appTagline: "To Communicate the Love of Christ",
  appLogo: "",
  followUpsLabel: "Follow-Ups",
  followUpsIcon: "",
  followUpsCoverImage: "",
  followUpsTextColor: "white",
  followUpsTextEffect: "shadow",
  followUpsOverlay: 55,
  defaultInactivityDays: 30,
  qrPosterCards: {},
  calendarCategoryColors: {},
  apptShortName: "On-the-Fly Connections",
  apptShortDuration: 20,
  apptLongName: "Weekday Connections",
  apptLongDuration: 60,
  emergencyPhone: "",
  churchPhone: "",
  allowPhotoPrivacy: false,
  gradientTopOpacity: 0.6,
  gradientBottomOpacity: 0.6,
};

// Apply gradient opacity CSS vars to document root as early as possible.
// Called synchronously on module load (with defaults) and again after each
// settings fetch so light-mode gradient wash reflects the SA-controlled
// global values with no visible flash of a stale opacity.
function _applyGradientVars(s) {
  if (typeof document === "undefined") return;
  const top = typeof s?.gradientTopOpacity === "number" ? s.gradientTopOpacity : 0.6;
  const bot = typeof s?.gradientBottomOpacity === "number" ? s.gradientBottomOpacity : 0.6;
  document.documentElement.style.setProperty("--kc-gradient-top-opacity", String(top));
  document.documentElement.style.setProperty("--kc-gradient-bottom-opacity", String(bot));
}
// Prime the CSS vars with defaults immediately at module load — this runs
// before the first React render, so light mode never paints with an
// undefined opacity value.
_applyGradientVars(DEFAULTS);

const Ctx = createContext({ settings: DEFAULTS, loaded: false, reload: async () => {}, update: async () => {} });

export function AppSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  // `loaded` flips true once the first fetch of public/app-settings resolves.
  // Consumers use this to hide the fallback crown logo until the real (custom)
  // appLogo arrives, preventing a flash of the default brand on every page load.
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await kcFetch(`${API_BASE}/public/app-settings`);
      if (res.ok) {
        const fresh = { ...DEFAULTS, ...(await res.json()) };
        setSettings(fresh);
        _applyGradientVars(fresh);
      }
    } catch (_) { /* fallback to defaults */ }
    setLoaded(true);
  }, []);

  const update = useCallback(async (patch) => {
    const res = await kcFetch(`${API_BASE}/admin/app-settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.detail || "Could not save settings");
    }
    const fresh = await res.json();
    const merged = { ...DEFAULTS, ...fresh };
    setSettings(merged);
    _applyGradientVars(merged);
    return fresh;
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return <Ctx.Provider value={{ settings, loaded, reload, update }}>{children}</Ctx.Provider>;
}

export function useAppSettings() {
  return useContext(Ctx);
}

// Convenience helper for resolving the logo source.
// • If `loaded` is explicitly `false`, returns null → caller should render nothing
//   (this is the anti-flash path: we hide the default crown until the real
//   settings come back from the backend).
// • If `appLogo` is set, returns its data-URL / URL.
// • Otherwise returns the bundled default.
// Callers that don't pass `loaded` get the legacy behaviour for back-compat.
export function resolveLogo(appLogo, loaded) {
  if (loaded === false) return null;
  return appLogo && appLogo.length > 0 ? appLogo : "/kingdom-connect-logo.png";
}

// AppLogo — render the logo with anti-flash behaviour.
// While settings are loading, shows a neutral blank placeholder of the same
// dimensions. Once settings resolve, shows the custom logo (if any) or the
// bundled fallback. Use this wherever the app logo is displayed.
export function AppLogo({ size = 48, className = "", alt, style, ...rest }) {
  const { settings, loaded } = useContext(Ctx);
  const baseStyle = {
    width: size,
    height: size,
    display: "inline-block",
    objectFit: "contain",
    transition: "opacity 0.2s ease-in",
    ...(style || {}),
  };
  if (!loaded) {
    // Neutral placeholder — exact same footprint, no visible image.
    return (
      <span
        aria-hidden="true"
        className={className}
        data-testid="app-logo-loading"
        style={{ ...baseStyle, opacity: 0, background: "transparent" }}
      />
    );
  }
  const src = settings.appLogo && settings.appLogo.length > 0
    ? settings.appLogo
    : "/kingdom-connect-logo.png";
  return (
    <img
      src={src}
      alt={alt || settings.appName || "Logo"}
      className={className}
      data-testid="app-logo"
      style={{ ...baseStyle, opacity: 1 }}
      {...rest}
    />
  );
}
